import { Component } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabel } from 'primeng/floatlabel';
import { ButtonModule } from 'primeng/button';
import { CreateUserGQL, CreateUserAttributes, AccountsForAutoCompleteGQL, AccountsForAutoCompleteFieldsFragment, CountryCode, Language } from 'src/app/graphql/gql';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ToastService } from 'src/app/toast/toast.service';
import { Router } from '@angular/router';
import { SelectModule } from 'primeng/select';
import { PasswordModule } from 'primeng/password';
import { LanguageService } from 'src/app/services/language.service';
import { AppValidators } from 'src/app/app-validators';
import { MessageModule } from 'primeng/message';
import { LanguageSelectComponent } from 'src/app/shared/language-select/language-select.component';
import { AppValidationHelpers } from 'src/app/app-validation-helpers';
import { CountrySelectorComponent } from 'src/app/shared/country-selector/country-selector.component';
import { CountryStateSelectorComponent, CountryStateValue } from 'src/app/shared/country-state-selector/country-state-selector.component';

@Component({
  selector: 'app-create-user',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FloatLabel,
    ButtonModule,
    AutoCompleteModule,
    SelectModule,
    PasswordModule,
    MessageModule,
    LanguageSelectComponent,
    CountrySelectorComponent,
    CountryStateSelectorComponent
  ],
  templateUrl: './create-user.component.html',
  styleUrl: './create-user.component.scss',
})
export class CreateUserComponent {
  accountSuggestions: AccountsForAutoCompleteFieldsFragment[] = []

  languageControl = new FormControl('', [Validators.required])

  form = new FormGroup({
    accountId: new FormControl('', [Validators.required]),
    title: new FormControl(''),
    firstname: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    lastname: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    street: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    company: new FormControl('', [Validators.required, Validators.minLength(2), AppValidators.validateWithoutPattern("^[0-9]+$")]),
    email: new FormControl('', [Validators.required, Validators.email]),
    zip: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(10)]),
    city: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    country: new FormControl('', [Validators.required]),
    // Holds the composite value of app-country-state-selector. Named after the server-side
    // field so remote validation errors on `countryState` still find this control.
    countryState: new FormControl<CountryStateValue>({ countryState: undefined, otherState: undefined }),
    phone: new FormControl('', [Validators.required, Validators.pattern(AppValidators.PHONE_REGEX)]),
    fax: new FormControl('', [Validators.pattern(AppValidators.PHONE_REGEX)]),
    login: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(255), Validators.pattern(AppValidators.LOGIN_REGEX)]),
    cellPhone: new FormControl('', [Validators.pattern(AppValidators.PHONE_REGEX)]),
    password: new FormControl('', [Validators.required, Validators.pattern(AppValidators.PASSWORD_REGEX)]),
    passwordConfirmation: new FormControl('', [Validators.required, AppValidators.validateEquals('password')]),
    passphrase: new FormControl('', [Validators.required]),
    answer: new FormControl('', [Validators.required]),
    language: this.languageControl,
    commercialRegisterNumber: new FormControl(''),
    taxNumberCompany: new FormControl(''),
    taxNumberPersonal: new FormControl(''),
    idCardNumber: new FormControl(''),
    allowedIpAddresses: new FormControl('')
  })

  titleOptions = [$localize`:user form|title mr:Mr`, $localize`:user form|title ms:Ms`];

  constructor(
    private createUserGQL: CreateUserGQL,
    private accountsForAutoCompleteGQL: AccountsForAutoCompleteGQL,
    private toastService: ToastService,
    private router: Router,
    private languageService: LanguageService
  ) { }

  /** Selected country as the enum the country state selector expects. */
  get selectedCountry(): CountryCode | undefined {
    const country = this.form.controls.country.value;
    return country ? country as CountryCode : undefined;
  }

  onSubmit() {
    this.form.markAllAsDirty()
    if (this.form.valid) {
      const { countryState, ...attributes } = this.form.value;
      this.createUserGQL.mutate({
        input: {
          ...attributes,
          // countryState is a non-null String! server-side, so send '' for countries without states.
          countryState: countryState?.countryState ?? '',
          otherState: countryState?.otherState
        } as CreateUserAttributes,
        language: this.languageService.getCurrentLanguage() as Language
      }).subscribe((result) => {
        switch(result.data?.createUser.__typename) {
          case "ValidationErrors": {
            // An error whose attribute has no control in this form has nowhere to render, so show
            // it as a toast rather than failing silently. See BNDDEV-10062.
            const unmapped = AppValidationHelpers.applyValidationErrors(result.data.createUser.validationMessages, this.form);
            const unmappedMessage = AppValidationHelpers.unmappedErrorMessage(unmapped);

            if (unmappedMessage !== null) {
              this.toastService.show('error', unmappedMessage);
            }

            break;
          }

          default:
            this.toastService.show('success', 'User created successfully')
            this.router.navigateByUrl('/reseller/users')
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.form.markAllAsTouched();
    }
  }

  getRemoteValidationMessages = AppValidationHelpers.getRemoteValidationMessages

  updateAccountSuggestions(event: AutoCompleteCompleteEvent) {
    this.accountsForAutoCompleteGQL.fetch({ query: event.query }).subscribe(result => {
      this.accountSuggestions = result.data.accounts.nodes?.filter(a => !!a) ?? [];
    });
  }

  accountsAutocompleteLabel(item: AccountsForAutoCompleteFieldsFragment): string {
    return item.company + ', ' + item.clientNumber?.toString().padStart(10, '0');
  }
}
